import { useMutation, useQuery } from '@apollo/client';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { Fab, Grid, Icon, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { Module } from '@thro-types/modules-selections';
import { ShibUser } from '@thro-types/users';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import ADD_STUDENT_SELECTION_MUTATION from 'gql/mutations/ModuleSelectionsService/addStudentSelection.gql';
import DISCHARGE_STUDENT_MUTATION from 'gql/mutations/ModuleSelectionsService/dischargeStudent.gql';
import UPDATE_STUDENT_SELECTION_MUTATION from 'gql/mutations/ModuleSelectionsService/updateStudentSelection.gql';
import STUDENT_SELECTION_QUERY from 'gql/queries/ModuleSelectionsService/getStudentSelection.gql';

import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import ContextMenu from 'app/components/ContextMenu';
import GQLGuard from 'app/components/GQLGuard';
import { SelectionState } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { StudentSelection } from 'app/containers/AWPM-FWPM/StudentView/models';
import { getCurrentSelectionState } from 'app/containers/AWPM-FWPM/helper';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

interface ModuleControlButtonsStudentProps {
  menuOpen: boolean;
  anchorRef: React.MutableRefObject<null>;
  setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userData: ShibUser;
  selection: any;
  module: Module;
}

function isSignOut(studentSelectionData, module, selection) {
  const modulePref = studentSelectionData?.getStudentSelection?.modulePreferences?.find(
    modPref => modPref.moduleId === parseInt(module.id, 10)
  );
  if (getCurrentSelectionState(selection) === SelectionState.SignOutPossible && !!modulePref) {
    return !modulePref?.studentDischarge;
  }
  return !!modulePref;
}

function isSignIn(selection, studentSelectionData) {
  if (getCurrentSelectionState(selection) === SelectionState.SignOutPossible) {
    return false;
  }
  return (
    selection.lengthOfTheWishlist >
    (studentSelectionData?.getStudentSelection?.modulePreferences?.length ?? 0)
  );
}

export const ControlButtonsStudent = ({
  menuOpen,
  anchorRef,
  setMenuOpen,
  userData,
  selection,
  module
}: ModuleControlButtonsStudentProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [signOutModalOpen, setSignOutModalOpen] = useState(false);
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const getStudentSelection = useQuery<
    StudentSelection,
    { userId: number; moduleSelectionId: number }
  >(STUDENT_SELECTION_QUERY, {
    context: { clientName: 'federation' },
    variables: {
      moduleSelectionId: parseInt(selection?.id, 10),
      userId: userData?.id
    }
  });

  const [dischargeStudent] = useMutation(DISCHARGE_STUDENT_MUTATION, {
    context: { clientName: 'federation' }
  });

  const [createStudentSelection] = useMutation<{ addStudentSelection: StudentSelection }>(
    ADD_STUDENT_SELECTION_MUTATION,
    {
      context: { clientName: 'federation' }
    }
  );
  const [updateStudentSelection] = useMutation<StudentSelection>(
    UPDATE_STUDENT_SELECTION_MUTATION,
    { context: { clientName: 'federation' } }
  );

  const handleDischarge = async id => {
    await dischargeStudent({
      variables: {
        modulePreferenceId: id
      },
      refetchQueries: [
        {
          query: STUDENT_SELECTION_QUERY,
          context: { clientName: 'federation' },
          variables: {
            moduleSelectionId: parseInt(selection?.id, 10),
            userId: userData?.id
          }
        }
      ]
    });
  };

  const handleSignInAndOut = async (id, modulePreferences, desiredNumberOfModules) => {
    if (id) {
      await updateStudentSelection({
        variables: {
          data: {
            id: parseInt(id, 10),
            desiredNumberOfModules: desiredNumberOfModules,
            modulePreferences: modulePreferences.map((modPref, index) => ({
              moduleId: parseInt(modPref.moduleId, 10),
              rank: index + 1
            }))
          }
        },
        refetchQueries: [
          {
            query: STUDENT_SELECTION_QUERY,
            context: { clientName: 'federation' },
            variables: {
              moduleSelectionId: parseInt(selection?.id, 10),
              userId: userData?.id
            }
          }
        ]
      })
        .then(() => {
          dispatch(
            AlertActions.success(t(translations.pages.studentView.alerts.wishlistSaveSuccess()))
          );
        })
        .catch(e => {
          dispatch(
            AlertActions.error(`${t(translations.pages.studentView.alerts.wishlistSaveFailed())}`)
          );
        });
    } else {
      try {
        await createStudentSelection({
          variables: {
            data: {
              userId: userData?.id,
              desiredNumberOfModules: selection.maxSelectedModules,
              modulePreferences: {
                moduleId: module.id,
                rank: 1
              },
              moduleSelectionId: parseInt(selection?.id, 10)
            }
          },
          refetchQueries: [
            {
              query: STUDENT_SELECTION_QUERY,
              context: { clientName: 'federation' },
              variables: {
                moduleSelectionId: parseInt(selection?.id, 10),
                userId: userData?.id
              }
            }
          ]
        });
        dispatch(
          AlertActions.success(t(translations.pages.studentView.alerts.wishlistSaveSuccess()))
        );
      } catch (e) {
        dispatch(
          AlertActions.error(`${t(translations.pages.studentView.alerts.wishlistSaveFailed())}`)
        );
      }
    }
  };

  const handleDischargeStudent = async modulePreferences => {
    try {
      await handleDischarge(
        parseInt(modulePreferences?.find(modPref => modPref.moduleId === module.id).id, 10)
      );
      dispatch(AlertActions.success(t(translations.pages.studentView.alerts.signOutSuccess())));
    } catch (e) {
      dispatch(AlertActions.error(`${t(translations.pages.studentView.alerts.signOutFailed())}`));
    }
  };

  return (
    <GQLGuard query={getStudentSelection} ignoreErrors>
      {studentSelectionData => {
        const isSignOutPossible: boolean =
          isSignOut(studentSelectionData, module, selection) && selection?.publishedAt != null;
        const isSignInPossible: boolean = isSignIn(selection, studentSelectionData);
        if (!isSignOutPossible && !isSignInPossible) {
          return null;
        }
        return (
          <Grid>
            <Fab
              className="action"
              onClick={e => {
                setMenuOpen(true);
                e.stopPropagation();
                e.preventDefault();
              }}
              ref={anchorRef}
              size="small"
              style={{ zIndex: 222 }}
            >
              <Icon
                className="fas fa-ellipsis-h fa-lg"
                style={{
                  width: '1em',
                  color: 'black'
                }}
              />
            </Fab>
            <ContextMenu
              className="module__controls"
              open={menuOpen}
              setOpen={setMenuOpen}
              anchorEl={anchorRef.current}
            >
              {isSignOutPossible ? (
                <MenuItem className="module__header__controls__container">
                  <Grid
                    container
                    className="module__header__controls__container"
                    onClick={e => {
                      setSignOutModalOpen(true);
                      setMenuOpen(false);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <Grid className="module__header__controls__container__icon" item xs={3}>
                      <ListItemIcon>
                        <PersonRemoveIcon
                          style={{ color: 'red', fontSize: '1.1em', width: 'auto' }}
                        />
                      </ListItemIcon>
                    </Grid>
                    <Grid item xs={9}>
                      <ListItemText primary={t(translations.pages.modules.signOut())} />
                    </Grid>
                  </Grid>
                </MenuItem>
              ) : (
                <MenuItem className="module__header__controls__container">
                  <Grid
                    container
                    className="module__header__controls__container"
                    onClick={e => {
                      setSignInModalOpen(true);
                      setMenuOpen(false);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <Grid className="module__header__controls__container__icon" item xs={3}>
                      <ListItemIcon>
                        <PersonAddAlt1Icon
                          style={{ color: 'lightblue', fontSize: '1.1em', width: 'auto' }}
                        />
                      </ListItemIcon>
                    </Grid>
                    <Grid item xs={9}>
                      <ListItemText primary={t(translations.pages.modules.signIn())} />
                    </Grid>
                  </Grid>
                </MenuItem>
              )}
            </ContextMenu>
            <ConfirmationDialog
              onClose={() => setSignOutModalOpen(false)}
              onConfirm={() => {
                getCurrentSelectionState(selection) !== SelectionState.SignOutPossible
                  ? handleSignInAndOut(
                      studentSelectionData?.getStudentSelection?.id,
                      studentSelectionData?.getStudentSelection?.modulePreferences?.filter(
                        modPref => modPref.moduleId !== module.id
                      ),
                      studentSelectionData?.getStudentSelection?.desiredNumberOfModules
                    )
                  : handleDischargeStudent(
                      studentSelectionData?.getStudentSelection?.modulePreferences
                    );
                setSignOutModalOpen(false);
              }}
              title={t(translations.pages.studentView.signOutDialogTitle())}
              open={signOutModalOpen}
            />
            <ConfirmationDialog
              onClose={() => setSignInModalOpen(false)}
              onConfirm={() => {
                !!studentSelectionData?.getStudentSelection
                  ? handleSignInAndOut(
                      studentSelectionData?.getStudentSelection?.id,
                      [
                        ...studentSelectionData?.getStudentSelection?.modulePreferences,
                        {
                          moduleId: module.id,
                          rank:
                            studentSelectionData?.getStudentSelection?.modulePreferences?.length + 1
                        }
                      ],
                      studentSelectionData?.getStudentSelection?.desiredNumberOfModules
                    )
                  : handleSignInAndOut(false, [], 0);
                setSignInModalOpen(false);
              }}
              title={t(translations.pages.studentView.signInDialog())}
              open={signInModalOpen}
            />
          </Grid>
        );
      }}
    </GQLGuard>
  );
};
